import React, { useMemo } from "react";
import addDays from "date-fns/addDays";
import addMonths from "date-fns/addMonths";
import areIntervalsOverlapping from "date-fns/areIntervalsOverlapping";
import format from "date-fns/format";
import isBefore from "date-fns/isBefore";
import isWithinInterval from "date-fns/isWithinInterval";
import parse from "date-fns/parseISO";
import { Row, Col } from "reactstrap";
import { CheckCircle } from "react-feather";

import Loader from "../../common/components/Loader";
import { dayOfMonthWithSuffix } from "../../common/helpers/date";

import { MarketingActivity as Activity } from "../../api";

interface MarketingActivitiesCalendarProps {
  activities?: Activity[];
  year: number;
  isLoading: boolean;
  handleSelect: (id: string) => any;
}

const getInterval: (item: Activity) => Interval | null = (item: Activity) => {
  const startIso = item.publicationDate ?? item.completedDate ?? item.deadlineDate;
  const endIso = item.completedDate ?? item.deadlineDate ?? item.publicationDate;
  if (startIso && endIso) {
    const start = parse(startIso);
    const end = parse(endIso);
    return { start, end };
  }
  return null;
}

const months = (() => {
  const arr = [];
  for (let i = 0; i < 12; i++) {
    arr.push(i);
  }
  return arr;
})();

const MarketingActivitiesCalendar: React.FC<MarketingActivitiesCalendarProps> = ({
  activities,
  year,
  isLoading,
  handleSelect,
}) => {

  const items = useMemo(
    () => activities || [],
    [activities]);

  return (
    <div className="position-relative">
      <Row>
        {months.map(m => {
          const startOfMonth = new Date(year, m, 1);
          const endOfMonth = addDays(addMonths(startOfMonth, 1), -1);
          const monthInterval: Interval = { start: startOfMonth, end: endOfMonth };
          const label = format(startOfMonth, "MMMM");
          const arr = items.filter(x => {
            const interval = getInterval(x);
            if (interval) {
              return isBefore(interval.start, interval.end)
                ? areIntervalsOverlapping(
                  monthInterval,
                  interval,
                  { inclusive: true })
                : isWithinInterval(interval.end, monthInterval);
            }
            return false;
          });
          return (
            <Col key={m} xl={3} lg={4} md={6} sm={12} className="mb-3 d-flex">
              <div className="bg-gradient box-shadow flex-grow-1 d-flex flex-column rounded">
                <div className="px-2 py-1 text-primary" style={{
                  borderTopLeftRadius: "24px",
                  borderTopRightRadius: "24px",
                }}>{label}</div>
                <div className="flex-grow-1 position-relative" style={{
                  borderBottomLeftRadius: "24px",
                  borderBottomRightRadius: "24px",
                  minHeight: "4em",
                }}>
                  {arr.length !== 0 && (
                    <ul className="mb-0 list-none list-bg-panel-light-even">
                      {arr.map(x => {
                        const interval = getInterval(x);
                        if (interval) {
                          return (
                            <li key={x.id} className="px-2 py-1 d-flex align-items-center">
                              <div className="position-relative flex-grow-1">
                                <div className="position-absolute text-truncate"
                                  style={{ inset: 0 }}>
                                  <a href={`#${x.id}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleSelect(x.id);
                                    }}>{x.name}</a>
                                </div>
                                &nbsp;
                              </div>
                              {(x.isActive || x.isCompleted) && isWithinInterval(interval.end, monthInterval)
                                ? !!x.isCompleted
                                  ? (<span className="ml-2 text-primary"><CheckCircle size={15} /></span>)
                                  : (<span className="ml-2">{dayOfMonthWithSuffix(interval.end)}</span>)
                                : (<>&nbsp;</>)}
                            </li>);
                        }
                        return void 0;
                      })}
                    </ul>)}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
      <Loader show={isLoading} background="panel-light" />
    </div>
  );
};

export default MarketingActivitiesCalendar;
